import React from 'react';
import { Box, Typography, Avatar, IconButton, useTheme } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import { User, useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { fetchCompanyLogoBlob } from '@/services/companyLogoService';

interface DrawerHeaderProps {
  user: User | undefined;
  initials?: string;
  isMobile: boolean;
  onProfileClick: (event: React.MouseEvent<HTMLElement>) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const DrawerHeader: React.FC<DrawerHeaderProps> = ({
  user,
  onProfileClick,
  isMobile,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const theme = useTheme();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();
  const [logoSrc, setLogoSrc] = React.useState<string | undefined>(undefined);
  const objectUrlRef = React.useRef<string | null>(null);

  const fallbackInitials = React.useMemo(() => {
    const name = user?.name || user?.nickname || user?.email || '';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }, [user]);

  const loadLogo = React.useCallback(async () => {
    if (!appUser?.id) {
      setLogoSrc(undefined);
      return;
    }
    try {
      const blob = await fetchCompanyLogoBlob(
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        true
      );
      if (!blob) {
        setLogoSrc(undefined);
        return;
      }
      const url = URL.createObjectURL(blob);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = url;
      setLogoSrc(url);
    } catch {
      setLogoSrc(undefined);
    }
  }, [appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup, selectedUserRole?.id]);

  React.useEffect(() => {
    loadLogo();
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [loadLogo, selectedUserRole?.id]);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 2,
        mb: 1,
        justifyContent: isCollapsed ? 'center' : 'flex-start'
      }}
    >
      {isCollapsed ? (
        // Collapsed state: show only expand button centered
        <IconButton
          onClick={onToggleCollapse}
          size="medium"
          sx={{ color: 'text.primary' }}
          aria-label="Expand sidebar"
        >
          <ChevronRight fontSize="medium" />
        </IconButton>
      ) : (
        // Expanded state: show full header with proper space allocation
        <>
          <Box
            onClick={onProfileClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0, // Allow shrinking
              flex: 1, // Take available space
              maxWidth: 'calc(100% - 48px)', // Reserve space for collapse button
              cursor: 'pointer',
              pl: 1.5,
              pr: 2,
              py: 0.75,
              borderRadius: 1,
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <Avatar
              src={logoSrc}
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)' 
                  : 'rgba(0, 0, 0, 0.06)',
                fontSize: '14px',
                fontWeight: 600,
                color: theme.palette.text.primary
              }}
            >
              {fallbackInitials}
            </Avatar>

            <Box sx={{ minWidth: 120, overflow: 'hidden', ml: 1.5, flexGrow: 1 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user?.name || user?.email}
              </Typography>
            </Box>
          </Box>

          {/* Collapse toggle (desktop only) - always visible */}
          {!isMobile && onToggleCollapse && (
            <IconButton
              onClick={onToggleCollapse}
              size="medium"
              sx={{
                flexShrink: 0,
                width: 40,
                height: 40,
                ml: 0.5,
                borderRadius: 1
              }}
              aria-label="Collapse sidebar"
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
};
